import type { VaultProvider, VaultPublicDataAll } from './fetchVaultsAll'
import type { InterfaceKind } from './interfaceKind'
import type { Denomination, YieldProfile } from './classification'

/**
 * The cross-provider subset of static vault metadata + share-price
 * inputs that every supported provider populates. Lifted out of the
 * per-provider types so consumers (e.g. a user-balance endpoint that
 * only needs to know decimals + share/asset ratio) don't have to
 * branch on provider key.
 *
 * Load-bearing fields here MUST be present in every provider's value
 * type — the structural constraint in `buildVaultLookup`'s
 * `addEntries<T extends {...}>` enforces this at compile time. If a
 * future provider drops one, the TS build fails.
 */
export interface VaultLookupEntry {
  /** Provider key — `fluid | gearbox | morpho | silo | euler-earn` */
  provider: VaultProvider
  /** Share token (vault) address, lowercased. */
  address: string
  /** Underlying ERC-20 address, lowercased. */
  underlying: string
  /** Share-token symbol (e.g. `fUSDC`, `dWETHV3`). */
  symbol: string
  /** Share-token name. */
  name: string
  /** Share decimals. By ERC-4626 convention equals underlying decimals. */
  decimals: number
  /** Underlying asset decimals. For plain ERC-4626 providers this equals
   *  `decimals`; for providers where the share token and underlying have
   *  different decimals (e.g. Lagoon: 18-decimal shares over USDC/WBTC),
   *  this is the underlying's own decimals. Absent ⇒ assume it equals
   *  `decimals`. Load-bearing for share→asset formatting. */
  assetDecimals?: number
  /** Total underlying held by the vault, raw integer string. */
  totalAssets: string
  /** Total shares minted, raw integer string. */
  totalSupply: string
  /** Vault interface family, when detected via ERC-165
   *  (`detectInterfaceKinds`). Absent ⇒ not probed; consumers should
   *  treat absent as `erc4626`. Drives deposit/withdraw + withdrawal-
   *  queue routing. */
  interfaceKind?: InterfaceKind
  /** NAV behaviour — `yield-bearing` (monotonic accrual: LST, savings,
   *  lending supply) vs `volatile` (trading/perp/leveraged strategy).
   *  Set by `stampVaultClassification`; absent ⇒ not classified. */
  yieldProfile?: YieldProfile
  /** Underlying denomination — `stable` (stablecoin underlying) vs
   *  `volatile`. Orthogonal to `yieldProfile`. */
  denomination?: Denomination
  /** Uniform share price, folded from the provider's `convertToAssets`/
   *  `pricePerShare` by `stampVaultClassification`. `sharePriceRaw` is the
   *  asset-scaled raw integer (`'0'` for empty); `sharePrice` is it formatted
   *  to underlying units; `sharePriceUSD` is `sharePrice * priceUsd`. Absent on
   *  hypercore/gmx. */
  sharePriceRaw?: string
  sharePrice?: number
  sharePriceUSD?: number
  /** Resolved branded icon URL — share-token logo, else underlying-asset logo.
   *  Carried from `stampVaultClassification`; absent ⇒ none resolved. */
  logoURI?: string
  /**
   * How a USER's position in this vault is read.
   *
   * Absent ⇒ `balanceOf(account)` on {@link address}, which is right for every
   * vault whose position IS a share-token balance — i.e. all but one.
   *
   * `savings-account` means the address is **not a token at all**: Frankencoin's
   * savings module is an internal ledger (`savings(address) → (saved, ticks)`)
   * and `balanceOf` REVERTS on it. That matters because the balance path runs
   * `allowFailure: true`, so the revert silently became a zero and a real
   * 162 ZCHF deposit rendered as an empty position. A missing balance that
   * looks like a legitimate zero is the worst shape this bug could take, which
   * is why the read is dispatched rather than probed.
   */
  balanceKind?: 'erc20' | 'savings-account'
}

/**
 * Flattens the per-provider maps from `VaultPublicDataAll` into a
 * single `vaultAddress → VaultLookupEntry` map.
 *
 * The per-provider maps key inconsistently — Fluid + Gearbox by
 * underlying, Morpho/Silo/Euler-Earn by vault address. We iterate
 * values and use each entry's `.address` field as the canonical key
 * so the resulting lookup is uniform regardless of provider keying.
 *
 * Pure transform — no I/O, no env. Cache wrappers live in callers
 * (e.g. worker-api's `vaultsShared.ts`).
 */
export function buildVaultLookup(
  data: VaultPublicDataAll,
): Map<string, VaultLookupEntry> {
  const map = new Map<string, VaultLookupEntry>()

  const addEntries = <
    T extends {
      address: string
      underlying: string
      symbol: string
      name: string
      decimals: number
      assetDecimals?: number
      totalAssets: string
      totalSupply: string
      yieldProfile?: YieldProfile
      denomination?: Denomination
      sharePriceRaw?: string
      sharePrice?: number
      sharePriceUSD?: number
      logoURI?: string
      balanceKind?: 'erc20' | 'savings-account'
    },
  >(
    bag: Record<string, T> | undefined,
    provider: VaultProvider,
  ) => {
    if (!bag) return
    for (const v of Object.values(bag)) {
      const addr = v.address.toLowerCase()
      // Ignore duplicate addresses from disagreeing providers — first
      // writer wins. Should not happen in practice (vault addresses
      // are globally unique on a given chain).
      if (map.has(addr)) continue
      map.set(addr, {
        provider,
        address: addr,
        underlying: v.underlying.toLowerCase(),
        symbol: v.symbol,
        name: v.name,
        decimals: v.decimals,
        // Defaults to share decimals for ERC-4626 providers (where the
        // two coincide); Lagoon sets it explicitly when they diverge.
        assetDecimals: v.assetDecimals ?? v.decimals,
        totalAssets: v.totalAssets,
        totalSupply: v.totalSupply,
        // Carried through from `stampVaultClassification` (set on the
        // provider objects before the lookup is built).
        yieldProfile: v.yieldProfile,
        denomination: v.denomination,
        sharePriceRaw: v.sharePriceRaw,
        sharePrice: v.sharePrice,
        sharePriceUSD: v.sharePriceUSD,
        logoURI: v.logoURI,
        balanceKind: v.balanceKind,
      })
    }
  }

  addEntries(data.fluid, 'fluid')
  addEntries(data.gearbox, 'gearbox')
  addEntries(data.morpho, 'morpho')
  addEntries(data.lista, 'lista')
  addEntries(data.silo, 'silo')
  addEntries(data['euler-earn'], 'euler-earn')
  addEntries(data.termmax, 'termmax')
  addEntries(data.lst, 'lst')
  addEntries(data.savings, 'savings')
  addEntries(data.lagoon, 'lagoon')
  addEntries(data['aave-earn'], 'aave-earn')
  addEntries(data.upshift, 'upshift')
  addEntries(data.yearn, 'yearn')
  // NOT added: hypercore, gmx, pendle, spectra — none of them is a share token over a
  // pool of assets, so `totalAssets`/`totalSupply`/`sharePrice` have no
  // meaning and every consumer of this map (share→asset math on user
  // balances) would be reading fabricated numbers. A Pendle or Spectra PT in
  // a wallet is a plain ERC-20 and resolves through the ordinary token-balance
  // path.

  return map
}
